"use client";

import { ArrowUpIcon } from "lucide-react";
import React from "react";
import Animate from "@/components/animations/Animate";
import { buttonVariants } from "@/components/ui/Button";
import { breakpointClasses, cn, splitBreakpointProps } from "@/lib/utils";
import type { ScrollToTopProps } from "./types";

const SCROLL_THRESHOLD = 300;

function ScrollToTop({ className, ...props }: ScrollToTopProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const { bp, native } = splitBreakpointProps(props);
  const classes = breakpointClasses(bp);

  const handleOnScroll = React.useCallback(() => {
    setIsVisible(window.scrollY > SCROLL_THRESHOLD);
  }, []);

  React.useEffect(() => {
    handleOnScroll();
    window.addEventListener("scroll", handleOnScroll);

    return () => {
      window.removeEventListener("scroll", handleOnScroll);
    };
  }, [handleOnScroll]);

  const scrollToTop = React.useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <Animate.Presence>
      {isVisible && (
        <Animate
          as="button"
          variant="fadeInBottom"
          onClick={scrollToTop}
          className={buttonVariants({
            className: cn(
              "fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-full",
              className,
              classes,
            ),
            variant: "secondary",
          })}
          aria-label="Scroll to top"
          {...native}
        >
          <ArrowUpIcon className="h-5 w-5" />
        </Animate>
      )}
    </Animate.Presence>
  );
}

ScrollToTop.displayName = "ScrollToTop";

export default ScrollToTop;
